import { format, isAfter } from "date-fns";
import { parseISOWithoutTime } from "helpers/date-helpers";
import { HELP, User } from "routes";
import {
  billingStatuses,
  ChangePaymentMethodModalNames,
  dateFnsFormats,
  discoProgramEligibility,
  membershipItemStates,
  nextGridProgramEligibility,
} from "rtr-constants";
import { HEAP_AUTH_TRIGGER_TYPES } from "helpers/heap-helpers";
import { isAuthorized } from "components/source/hoc/with-user-data";
import { isPauseWithItemsTier } from "helpers/membership-tier-helpers";
import {
  isPausedWithoutItems,
  isPausedWithItems,
  isFuturePausedWithoutItems,
  isFuturePausedWithoutItemsWithinThreeDays,
  isFutureCanceled,
} from "helpers/membership-plan-helpers";
import { useSelector, useDispatch } from "react-redux";
import AddShipmentModal from "components/source/membership/modals/AddShipmentModal/AddShipmentModalWrapper";
import AuthActions from "actions/auth-actions";
import MembershipHelpers, { isSubscriptionMember } from "helpers/membership-helpers";
import sharedActions from "actions/shared-actions";
import { addCacheBusterToUrl } from "helpers/location-helpers";

const NO_SLOTS_CLASSNAME = "no-slots";

// This hook is essentially a lift'n'shift of MembershipFormFieldsBillingStatus which is
// a legacy component that is aware of all the weird and wonderful error states that
// a member can find themselves in. There is also logic copied from the legacy
// `withMembershipStateInterface` function which wraps the old membershipFormFields component.
// Unlike the legacy components, because this is a hook, there is no rendering, the function
// simply returns data that the parent component can use when rendering.
export default function useMembershipError() {
  const membershipState = useSelector(state => state.membershipState);
  const staticProduct = useSelector(state => state.staticProduct);
  const userData = useSelector(state => state.userData);
  const dispatch = useDispatch();

  if (!membershipState || !isSubscriptionMember(userData)) {
    return null;
  }

  const userNotEligibleForProduct = () => {
    const programEligibilityOfProduct = staticProduct?.programEligibility;
    const inventoryEligibilitiesOfMember = membershipState?.inventoryEligibilities;
    // @TODO: Uncomment isSubscriptionLens when lenses are added to universal PDP

    // isSubscriptionLens(userData) &&
    return (
      isSubscriptionMember(userData) &&
      nextGridProgramEligibility[inventoryEligibilitiesOfMember?.[0]] === nextGridProgramEligibility.rtr_update &&
      !programEligibilityOfProduct.includes(discoProgramEligibility.rtr_update)
    );
  };

  const dispatchOnAuthorizedOrPromptLogin = action => {
    if (isAuthorized(userData)) {
      dispatch(action);
    } else {
      dispatch(AuthActions.showAuthModal({ triggeredBy: HEAP_AUTH_TRIGGER_TYPES.AUTH_PROMPT }));
    }
  };

  const onOpenAddShipmentModal = () => {
    dispatchOnAuthorizedOrPromptLogin(sharedActions.displayModal(AddShipmentModal.modalName));
  };

  const onOpenRetryModal = () => {
    dispatchOnAuthorizedOrPromptLogin(
      sharedActions.displayModal(ChangePaymentMethodModalNames.changePaymentMethodFromReturnFlow)
    );
  };

  const renderProductDrawerError = (additionalClassName, text, ctaText, errorURL, onClickCta) => {
    return {
      type: "notification",
      additionalClassName: `membership-form-fields__${additionalClassName}`,
      text,
      ctaText,
      errorURL,
      onClickCta,
    };
  };

  const renderAddSwapCta = () => {
    return {
      type: "swap_notification",
    };
  };

  const renderNoOpenPickSlotsErrorMessaging = () => {
    const nextPickDate = MembershipHelpers.fetchNextPickDate(membershipState);
    const formattedNextPickDate = format(parseISOWithoutTime(nextPickDate), dateFnsFormats.day);
    const text = `You can swap again anytime after ${formattedNextPickDate}.`;

    return renderProductDrawerError(NO_SLOTS_CLASSNAME, text);
  };

  const renderOverdueItemMemberState = () => {
    const additionalClassName = "retry-member";
    const text = "You have overdue items still at home. You won’t be able to swap again until they’re returned.";

    return renderProductDrawerError(additionalClassName, text);
  };

  const renderRetryMemberState = () => {
    const additionalClassName = "retry-member";
    const text = "Your monthly payment didn't go through. Make your payment now to keep your account active.";
    const onClickCta = onOpenRetryModal;
    const ctaText = "Update Now";

    return renderProductDrawerError(additionalClassName, text, ctaText, null, onClickCta);
  };

  const renderIneligiblePlanMessaging = () => {
    let text, onClickCta;
    const ctaText = "Add a Shipment.";

    if (MembershipHelpers.isEligibleForImmediateSwapUpgrade(membershipState)) {
      text = "This style is not included in your plan. To upgrade and access the full closet, ";
      onClickCta = onOpenAddShipmentModal;

      return renderProductDrawerError(NO_SLOTS_CLASSNAME, text, ctaText, null, onClickCta);
    }

    text = "This style is not included in your plan. To upgrade and access premium inventory, ";
    const errorURL = User.Settings;

    return renderProductDrawerError(NO_SLOTS_CLASSNAME, text, ctaText, errorURL, null);
  };

  const renderFutureCancelMemberState = () => {
    const additionalClassName = "canceled-member";
    const text = "Your membership has been cancelled. Ready to rent again?";
    const ctaText = "Get in Touch";
    const errorURL = `${addCacheBusterToUrl(HELP.CONTACT)}`;

    return renderProductDrawerError(additionalClassName, text, ctaText, errorURL);
  };

  const renderFuturePausedMemberState = () => {
    const { futureMembershipTierRevision, membershipPausedOn, membershipResumedOn, tierChangeStartedOn } =
      membershipState;
    let additionalClassName, text, errorCTA, errorURL;

    /** Checks 2 Future Paused states:
     * 1) Future Paused with Items
     * 2) Future Paused without Items — includes checks to ensure membershipPausedOn is actually AFTER the current date and
     * if member has no remaining shipments or is pausing within 3 days
     * */
    if (isPauseWithItemsTier(futureMembershipTierRevision) && tierChangeStartedOn) {
      additionalClassName = "future-pause-with-items-member";
      const formattedPauseBeginDate = format(parseISOWithoutTime(tierChangeStartedOn), dateFnsFormats.day);
      text = `Your membership will be paused starting ${formattedPauseBeginDate}.`;
      errorCTA = "Manage Membership";
      errorURL = User.Settings;
    } else if (
      membershipPausedOn &&
      membershipResumedOn &&
      isAfter(parseISOWithoutTime(membershipPausedOn), new Date()) &&
      (isFuturePausedWithoutItemsWithinThreeDays(membershipState.membershipPausedOn) ||
        !MembershipHelpers.hasShipmentsCount(membershipState))
    ) {
      const formattedPauseBeginDate = format(parseISOWithoutTime(membershipPausedOn), dateFnsFormats.day);
      const formattedResumeDate = format(parseISOWithoutTime(membershipResumedOn), dateFnsFormats.day);
      additionalClassName = "future-paused-member";
      text = `Your membership will be paused from ${formattedPauseBeginDate} to ${formattedResumeDate}.`;
    } else {
      return null;
    }

    return renderProductDrawerError(additionalClassName, text, errorCTA, errorURL);
  };

  const renderPausedMemberState = () => {
    /** Checks 2 Actively Paused states:
     * 1) Currently Paused with Items
     * 1) Currently Paused without Items
     * */
    const { nextBillingDate, previousTierResumesOn } = membershipState;

    let additionalClassName, formattedResumeDate, text, errorCTA, errorURL;

    if (previousTierResumesOn) {
      additionalClassName = "paused-with-items-member";
      formattedResumeDate = format(parseISOWithoutTime(previousTierResumesOn), dateFnsFormats.day);
      text = `Your membership is paused until ${formattedResumeDate}.`;
      errorCTA = "Manage Membership";
      errorURL = User.Settings;
    } else {
      additionalClassName = "paused-member";
      formattedResumeDate = format(parseISOWithoutTime(nextBillingDate), dateFnsFormats.day);
      text = `Your membership is paused until ${formattedResumeDate}.`;
      errorCTA = "Manage Membership";
      errorURL = User.Settings;
    }

    return renderProductDrawerError(additionalClassName, text, errorCTA, errorURL);
  };

  const isPausedWithoutItemsWithinThreeDays = isFuturePausedWithoutItemsWithinThreeDays(
    membershipState.membershipPausedOn
  );

  if (
    MembershipHelpers.countOfItemTypes(membershipState, membershipItemStates.OVERDUE) > 0 &&
    !MembershipHelpers.isEligibleToPick(membershipState)
  ) {
    return renderOverdueItemMemberState();
  }

  if (membershipState.billingStatus === "PAUSED") {
    return renderPausedMemberState();
  }

  if (
    (isPauseWithItemsTier(membershipState.futureMembershipTierRevision) ||
      isPausedWithoutItems(membershipState) ||
      isPausedWithItems(membershipState) ||
      isFuturePausedWithoutItems(membershipState.membershipPausedOn)) &&
    Number.isInteger(membershipState.monthlyShipmentLimit)
  ) {
    if (isPausedWithoutItemsWithinThreeDays || !MembershipHelpers.isEligibleForImmediateSwapUpgrade(membershipState)) {
      return renderFuturePausedMemberState();
    }
  }

  if (isFutureCanceled(membershipState.membershipEndedOn)) {
    return renderFutureCancelMemberState();
  }

  if (membershipState.billingStatus === billingStatuses.RETRY) {
    return renderRetryMemberState();
  }

  if (userNotEligibleForProduct()) {
    return renderIneligiblePlanMessaging();
  }

  // If there are no pick slots open or a future legacy pause user is more than 3 days from their pause
  if (
    !MembershipHelpers.fetchNumOpenSlots(membershipState) &&
    (!isFuturePausedWithoutItems(membershipState.membershipPausedOn) || !isPausedWithoutItemsWithinThreeDays)
  ) {
    if (MembershipHelpers.isEligibleForImmediateSwapUpgrade(membershipState)) {
      return renderAddSwapCta();
    } else if (
      MembershipHelpers.isFixedSwaps(membershipState) &&
      !MembershipHelpers.hasShipmentsCount(membershipState)
    ) {
      return renderNoOpenPickSlotsErrorMessaging();
    }
  }

  return null;
}
